import { useState, useEffect, useRef } from "react";
import { consolidateTickets, shouldWheel, applyExactaSingleMin, comboToXpress, amwagerComboField, actualPlayTotal, singleMinNote } from "../lib/ticketConsolidation";

// Temporarily hidden while we accumulate a few days of live 1MTP odds. Flip to true to re-enable.
const SHOW_OVERBET_FAV = true;

import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import "../styles/saddlecloth.css";
import { useAuth } from "../contexts/AuthContext";
import { useTriPlaysDay } from "../lib/useTriPlays";
import { AppMenu } from "./AppMenu";
import BetMakerPreferences from "./BetMakerPreferences";
import { AdwButton } from "./AdwButton";
import { TriBetLink } from './TriBetLink';
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import { MultiPoolResults } from "./MultiPoolResults";
import { RacePayoffs } from "./RacePayoffs";
import { Loader2, Info, Download, RefreshCw, User, LogOut, Key, BookOpen, Upload, PlayCircle, ArrowLeftRight, TrendingUp, Zap } from "lucide-react";
import TopPerformers from "./TopPerformers";
import { formatML } from "../lib/odds";
import TrialBadge from "./TrialBadge";

import { overbetTip } from "../lib/overbetStats";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safari (incl. iOS) does NOT render box-shadow/ring on table rows (<tr>) — a
// long-standing WebKit bug — so the Strong/Fade row ring is invisible there.
// Detect Safari to show an explicit text badge as a fallback ONLY on Safari;
// Chrome/Firefox/Edge keep the ring-only look.
const IS_SAFARI = typeof navigator !== "undefined"
  && /^((?!chrome|chromium|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent || "");

// Format date as the ET racing day (YYYY-MM-DD), anchored to America/New_York
// with a 4 AM ET rollover so it matches the backend _today(). NOT browser-local
// or UTC — a UTC date here made late-evening ET viewers request tomorrow's card
// (which has no races) → "no tracks available".
const formatDate = (date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hour12: false,
  }).formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const y = parseInt(parts.year, 10);
  const m = parseInt(parts.month, 10);
  const d = parseInt(parts.day, 10);
  let h = parseInt(parts.hour, 10);
  if (h === 24) h = 0; // en-US renders midnight as 24
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (h < 4) dt.setUTCDate(dt.getUTCDate() - 1); // before 4 AM ET -> previous racing day
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

// Relative "time ago" label for the live-odds refresh indicator
const formatAgo = (date) => {
  if (!date) return "";
  const sec = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  return `${min}m ago`;
};

// Saddle-cloth badge class for a program number (traditional post-position colors)
const pnClass = (n) => {
  const num = parseInt(n, 10);
  return `pn-badge ${num >= 1 && num <= 24 ? `pn${num}` : "pn-default"}`;
};

// Generate Amwager format download
// Format: ,,YYYYMMDD,USA,TRACK,TRACKSUFFIX,WAGERTYPE,,RACE,COMBO,USD,AMOUNT,,,
const generateAmwagerFormat = (track, race, pool, tickets, raceDate, wheel = true, base, ticketMin) => {
  const wagerTypeNames = { 'EX': 'EX', 'TRI': 'TRI', 'SPR': 'SPR' };
  const formattedDate = raceDate.replace(/-/g, ''); // YYYYMMDD

  // Split track code - last character goes to separate field
  const trackPrefix = track.slice(0, -1);
  const trackSuffix = track.slice(-1);

  // Straight bets (WIN/PLC) pass through unchanged; exotic combos get consolidated.
  const straight = [];
  const exotic = [];
  tickets.forEach((t) => {
    if (t.combo.startsWith('WIN,')) straight.push(`,,${formattedDate},USA,${trackPrefix},${trackSuffix},WIN,,${race},${t.combo.split(',')[1]},USD,${t.amount.toFixed(2)},,,`);
    else if (t.combo.startsWith('PLC,')) straight.push(`,,${formattedDate},USA,${trackPrefix},${trackSuffix},PLC,,${race},${t.combo.split(',')[1]},USD,${t.amount.toFixed(2)},,,`);
    else exotic.push(t);
  });
  const wagerType = wagerTypeNames[pool] || pool;
  const cons = consolidateTickets(exotic, wheel);
  applyExactaSingleMin(cons, pool, base, ticketMin);
  const exoticLines = cons.map((t) => `,,${formattedDate},USA,${trackPrefix},${trackSuffix},${wagerType},,${race},${amwagerComboField(t.legs)},USD,${t.amount.toFixed(2)},,,`);
  return [...straight, ...exoticLines].join('\n');
};

// Generate XpressBet format download
// Format: YYYY-MM-DD,TRACK,RACE,WAGERTYPE,COMBO,AMOUNT,,,WHEEL
const generateXpressBetFormat = (track, race, pool, tickets, raceDate, wheel = true, base, ticketMin) => {
  const wagerTypeNames = { 'EX': 'EXACTA', 'TRI': 'TRIFECTA', 'SPR': 'SUPERFECTA' };

  const straight = [];
  const exotic = [];
  tickets.forEach((t) => {
    if (t.combo.startsWith('WIN,')) straight.push(`${raceDate},${track},${race},WIN,${t.combo.split(',')[1]},${t.amount.toFixed(2)},,,WHEEL`);
    else if (t.combo.startsWith('PLC,')) straight.push(`${raceDate},${track},${race},PLACE,${t.combo.split(',')[1]},${t.amount.toFixed(2)},,,WHEEL`);
    else exotic.push(t);
  });
  const wagerType = wagerTypeNames[pool] || pool;
  const cons = consolidateTickets(exotic, wheel);
  applyExactaSingleMin(cons, pool, base, ticketMin);
  const exoticLines = cons.map((t) => `${raceDate},${track},${race},${wagerType},${comboToXpress(t.legs)},${t.amount.toFixed(2)},,,WHEEL`);
  return [...straight, ...exoticLines].join('\n');
};

// Download helper
const downloadFile = (content, filename) => {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Log download for tracking
const logDownload = async (track, raceNumber, pool, format, ticketCount, totalWager, raceDate, tickets, inputs) => {
  try {
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quickpick/log-download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        track,
        race_number: raceNumber,
        race_date: raceDate,
        pool,
        format,
        ticket_count: ticketCount,
        total_wager: totalWager,
        tickets: tickets || [],
        inputs: inputs || null,
      })
    });
  } catch (err) {
    // Silent fail - don't interrupt user experience
  }
};

// Convert time string from EST to user's local timezone
const formatLocalTime = (timeStr, dateStr) => {
  if (!timeStr) return "";
  try {
    // The feed may send a full ISO/UTC timestamp ("2026-06-25T16:35:00+00:00") or
    // the legacy wall-clock "HH:MM". Handle both; always render in US/Eastern.
    if (!/^\d{1,2}:\d{2}$/.test(String(timeStr).trim())) {
      const d = new Date(timeStr);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit" });
    }
    // Legacy "HH:MM" — interpret as US/Eastern wall-clock for the given date.
    const dateToUse = dateStr || formatDate(new Date());
    const estDateStr = `${dateToUse}T${timeStr.padStart(5, '0')}:00`;
    const month = new Date(dateToUse).getMonth();
    const offset = month >= 2 && month <= 10 ? '-04:00' : '-05:00';
    const d = new Date(estDateStr + offset);
    if (isNaN(d.getTime())) return ""; // guard: missing/bad post-time -> no "Invalid Date"
    return d.toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return "";
  }
};

// Calculate Minutes To Post (MTP) from a time string
const calculateMTP = (timeStr, dateStr) => {
  if (!timeStr) return null;
  try {
    let postTime;
    if (!/^\d{1,2}:\d{2}$/.test(String(timeStr).trim())) {
      // Full ISO/UTC timestamp — already an exact instant.
      postTime = new Date(timeStr);
    } else {
      const dateToUse = dateStr || formatDate(new Date());
      const estDateStr = `${dateToUse}T${timeStr.padStart(5, '0')}:00`;
      const month = new Date(dateToUse).getMonth();
      const offset = (month >= 2 && month <= 10) ? '-04:00' : '-05:00';
      postTime = new Date(estDateStr + offset);
    }
    if (isNaN(postTime.getTime())) return null;
    const diffMs = postTime.getTime() - new Date().getTime();
    return Math.round(diffMs / 60000);
  } catch (e) {
    return null;
  }
};

// Format MTP display string
const formatMTP = (timeStr, dateStr) => {
  const mtp = calculateMTP(timeStr, dateStr);
  if (mtp === null) return { text: "", isUrgent: false };
  if (mtp < 0) return { text: "POST", isUrgent: false };
  if (mtp === 0) return { text: "NOW", isUrgent: true };
  if (mtp <= 2) return { text: `${mtp} MTP`, isUrgent: true };
  return { text: `${mtp} MTP`, isUrgent: false };
};

// Normalize combination string for matching (remove spaces, convert / to -, lowercase)
const normalizeCombo = (combo) => {
  if (!combo) return "";
  return combo.toString().trim().replace(/\s+/g, "").replace(/\//g, "-").toLowerCase();
};

// Map pool code to pool name for payout lookup
const mapPoolToName = (poolCode) => {
  const mapping = {
    'EX': 'exacta',
    'TRI': 'tri',
    'SPR': 'spr',
    'WIN': 'win',
    'PLC': 'plc',
    'SHW': 'shw',
    'DBL': 'dbl'
  };
  return mapping[poolCode?.toUpperCase()] || poolCode?.toLowerCase() || "";
};

// Canonicalize any pool code (raw feed code OR normalized name) to the lowercase
// name used for payout matching — resilient to backend returning "EX" or "exacta".
const POOL_CANON = {
  WIN: "win", PLC: "place", PLACE: "place", SHW: "show", SHOW: "show",
  EX: "exacta", EXACTA: "exacta", TRI: "tri", TRIFECTA: "tri",
  SPR: "spr", SUP: "spr", SUPERFECTA: "spr", DBL: "dbl",
  PK3: "p3", P3: "p3", PK4: "p4", P4: "p4", PK5: "p5", P5: "p5",
  PK6: "p6", P6: "p6", QU: "quinella", OMN: "omni", PEN: "pen",
};
const normPool = (c) => POOL_CANON[(c || "").toUpperCase()] || (c || "").toLowerCase();


// Short-field floor: <=3 active (non-scratched) runners => no Exacta;
// <=4 => no Trifecta/Superfecta. Mirrors backend config.pool_offered_at_field_size.
const POOL_MIN_FIELD = { EX: 4, TRI: 5, SPR: 5, SUP: 5 };
const poolOfferedAtField = (pool, fs) =>
  !fs || fs >= (POOL_MIN_FIELD[(pool || "").toUpperCase()] || 0);

export const QuickPickTester = () => {
  const { user, logout, hasBetmaker, hasExactaValue } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [selectedRace, setSelectedRace] = useState("");
  const [selectedPool, setSelectedPool] = useState(() => {
    return localStorage.getItem("quickpick_pool") || "";
  });
  const [consolThresholds, setConsolThresholds] = useState({ EX: 0, TRI: 0, SPR: 0 });
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/quickpick/consolidation-settings`);
        if (r.data?.thresholds) setConsolThresholds(r.data.thresholds);
      } catch { /* keep defaults */ }
    })();
  }, []);
  // Collapse combos into wheels only when the pool's exotic-ticket count exceeds the
  // admin threshold (boxing + $2 exacta min always apply inside the builders).
  const wheelFor = (pool, tickets, data) => {
    const exotic = (tickets || []).filter((t) => !t.combo.startsWith("WIN,") && !t.combo.startsWith("PLC,"));
    return shouldWheel(exotic.length, consolThresholds[pool], data?.poolMinimum, data?.ticketMin);
  };

  const [straightBetOption, setStraightBetOption] = useState("none");
  const [myKeysOnly, setMyKeysOnly] = useState(true);
  const [showStraightBetInfo, setShowStraightBetInfo] = useState(false);
  const [betAmount, setBetAmount] = useState(() => {
    // Load from localStorage or default to 20
    return localStorage.getItem("quickpick_betamt") || "20";
  });
  const [loading, setLoading] = useState(false);
  const [loadingRaces, setLoadingRaces] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [result, setResult] = useState(null);
  const [exactaEv, setExactaEv] = useState(null);
  const [exactaEvOn, setExactaEvOn] = useState(true);
  const [error, setError] = useState(null);
  const [entries, setEntries] = useState([]);
  const [racePayouts, setRacePayouts] = useState([]);
  const [poolEligibility, setPoolEligibility] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [lastRefresh, setLastRefresh] = useState(null);
  const [entriesRefreshedAt, setEntriesRefreshedAt] = useState(null);
  const [mtpTick, setMtpTick] = useState(0); // Force re-render every 30s for MTP updates

  // User preferences (Default Settings) — same modal as iPro, reachable from the IntelliTix menu.
  const DEFAULT_PREFS = { defaultApp: "intellitix", defaultMode: "freeflow", bankroll: 50, poolDefaults: {}, pools: ["TRI"], oddsSource: "proj", blendPct: 0, perPool: false, roundUp: true, rememberLastUsed: false, rememberRaceInputs: true, showTopUp: true, theme: "bright" };
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [betmakerEnabled, setBetmakerEnabled] = useState(false);
  
  // Refs for auto-refresh timer
  const refreshTimerRef = useRef(null);
  const entriesTimerRef = useRef(null);
  const selectedPostTimeRef = useRef(null);
  const currentTrackRef = useRef(selectedTrack);

  // Save betAmount to localStorage when it changes
  useEffect(() => {
    if (betAmount) {
      localStorage.setItem("quickpick_betamt", betAmount);
    }
  }, [betAmount]);

  // Save pool to localStorage when it changes
  useEffect(() => {
    if (selectedPool) {
      localStorage.setItem("quickpick_pool", selectedPool);
    }
  }, [selectedPool]);

  // Admin visibility mode for the Exacta Expected-Value chiclet (Env tab).
  useEffect(() => {
    axios.get(`${API}/quickpick/exacta-ev-setting`)
      .then((r) => setExactaEvOn((r.data?.mode || 'entitled') !== 'off'))
      .catch(() => {});
  }, []);

  // Load saved Default Settings so the preferences modal opens with current values.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/betmaker/preferences`, { withCredentials: true });
        setPrefs({ ...DEFAULT_PREFS, ...(r.data?.preferences || {}) });
        setBetmakerEnabled(!!r.data?.betmakerEnabled);
      } catch { /* not logged in / no prefs -> keep defaults */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePrefs = async (form) => {
    try {
      const r = await axios.put(`${API}/betmaker/preferences`, form, { withCredentials: true });
      setPrefs({ ...DEFAULT_PREFS, ...(r.data?.preferences || {}) });
      setPrefsOpen(false);
      toast.success("Default settings saved");
    } catch {
      toast.error("Could not save settings");
    }
  };

  // Get races for selected track
  const selectedTrackData = tracks.find((t) => t.code === selectedTrack);
  const allRaces = selectedTrackData?.races || [];
  const currentRace = selectedTrackData?.currentRace || 1;
  
  // Show all races (don't filter), but auto-select currentRace
  const isToday = selectedDate === formatDate(new Date());
  const races = allRaces;

  // Update MTP display every 45 seconds (only when viewing today's races)
  useEffect(() => {
    if (!isToday) return;
    const mtpInterval = setInterval(() => {
      setMtpTick(t => t + 1);
    }, 45000); // Update every 45 seconds
    return () => clearInterval(mtpInterval);
  }, [isToday]);

  // Build "UP NEXT" data — matches BetMaker exactly: each track's first race
  // that is neither official nor cancelled (race-number order), sorted by post time.
  // Advancement is driven by the race going OFFICIAL (status FINAL)/CANCELLED —
  // NOT by MTP/post-time and NOT by the `final` boolean (which can flip early).
  const upNextTracks = tracks
    .map((track) => {
      const races = track.races || [];
      // Skip a race stuck at POST (no results file) when a LATER race is already
      // FINAL — a later official race proves this one ran (feed gap on its results).
      const laterFinal = (i) => races.slice(i + 1).some((r) => r.status === "FINAL");
      // Unstick a stuck race when the NEXT sequential (non-cancelled) race has a feed
      // cycle at <=18 MTP. `mtp` is null until that race actually cycles, so races that
      // haven't started can't trigger a clear. Scheduled post times are approximate and
      // are NOT used here.
      const nextCycling = (i) => {
        const nxt = races.slice(i + 1).find((r) => r.status !== "CANCELLED" && !r.cancelled);
        return !!nxt && nxt.mtp != null && nxt.mtp <= 18;
      };
      const next = races.find((r, i) => r.status !== "FINAL" && r.status !== "CANCELLED" && !r.cancelled && !laterFinal(i) && !nextCycling(i));
      if (!next) return null;
      return {
        code: track.code,
        name: track.name,
        currentRace: next.raceNumber,
        postTime: next.postTime || "",
        fieldSize: next.fieldSize || 0,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      // Sort by post time (soonest first)
      if (!a.postTime) return 1;
      if (!b.postTime) return -1;
      return a.postTime.localeCompare(b.postTime);
    });

  // Strong-key "+ROI Trifecta Play" detection for the Up Next chiclets. Opting in
  // from IntelliTix hands the play off to iPro (BetMaker) via sessionStorage.
  const navigate = useNavigate();
  const triDay = useTriPlaysDay(selectedDate);
  const triPlays = triDay.byKey;
  const optInTriRace = (trackCode, raceNumber, spec) => {
    try {
      sessionStorage.setItem("triPlayOptIn", JSON.stringify({ track: trackCode, race: raceNumber, date: selectedDate, spec }));
    } catch { /* ignore */ }
    navigate("/betmaker");
  };
  const optInTri = (t, spec) => optInTriRace(t.code, t.currentRace, spec);

  // Get pools for selected race
  const selectedRaceData = races.find(
    (r) => r.raceNumber === parseInt(selectedRace)
  );
  const pools = selectedRaceData?.pools || [];

  // Filter to only single-leg vertical pools (exacta, tri, spr)
  const verticalPools = pools.filter((p) => {
    const code = p.code?.toUpperCase();
    const name = p.name?.toLowerCase();
    const isVertical = ["EX", "TRI", "SPR", "SUP"].includes(code) || 
                       ["exacta", "tri", "spr", "superfecta"].includes(name);
    // If legs not provided, assume single-leg (1)
    const isSingleLeg = p.legs === undefined || p.legs === 1 || p.legs === "1";
    return isVertical && isSingleLeg;
  });

  // Build pool dropdown options (singles + combos), filtered by eligibility.
  // Uses poolEligibility from /entries (offered + standard/override). Falls back
  // to offered vertical pools (singles only) until eligibility loads.
  const POOL_LABELS = { EX: "Exacta", TRI: "Trifecta", SPR: "Superfecta" };
  const POOL_COMBOS = [
    ["EX", "TRI"],
    ["EX", "SPR"],
    ["TRI", "SPR"],
    ["EX", "TRI", "SPR"],
  ];
  const poolOptions = (() => {
    const elig = poolEligibility;
    const offeredCodes = verticalPools
      .map((p) => {
        const c = p.code?.toUpperCase();
        return c === "SUP" ? "SPR" : c;
      })
      .filter((c) => ["EX", "TRI", "SPR"].includes(c));

    // Which single pools to show
    const singles = ["EX", "TRI", "SPR"].filter((c) =>
      elig ? elig[c]?.show : offeredCodes.includes(c)
    );
    const reqKey = (c) => !!(elig && elig[c]?.requiresKey);

    const opts = singles.map((c) => ({
      value: c,
      pools: [c],
      label: POOL_LABELS[c],
      requiresKey: reqKey(c),
    }));

    // Combos: only when every constituent pool is shown
    POOL_COMBOS.forEach((combo) => {
      if (combo.every((c) => singles.includes(c))) {
        opts.push({
          value: combo.join("+"),
          pools: combo,
          label: combo.map((c) => POOL_LABELS[c]).join(" + "),
          requiresKey: combo.some((c) => reqKey(c)),
        });
      }
    });
    return opts;
  })();

  const currentPoolOption = poolOptions.find((o) => o.value === selectedPool) || null;

  // "No usable data" gate: when most runners lack Beyer figures (avgBeyer2 = 0 for
  // > 60% of the field) AND there are no usable projected odds AND no usable pool
  // rankings (all pool scores zero / -99 — e.g. a not-yet-carded race like SAR R1),
  // require the user to designate keys instead of auto-selecting on jtStats alone.
  const insufficientDataRequiresKey = (() => {
    const active = entries.filter((e) => !e.scratched);
    if (active.length === 0) return false;
    const beyerZero = active.filter((e) => !e.avgBeyer2 || e.avgBeyer2 === 0).length;
    if (beyerZero / active.length <= 0.6) return false;
    const noProjOdds = active.every((e) => !e.projOdds || e.projOdds <= 0);
    if (!noProjOdds) return false;
    const poolUsable = (field) => {
      const vals = active.map((e) => e[field]);
      const allValid = vals.every((v) => v !== -99 && v != null);
      const hasVariance = new Set(vals).size > 1;
      const notAllZero = !vals.every((v) => v === 0);
      return allValid && hasVariance && notAllZero;
    };
    const anyPoolUsable = ["p3Score", "p4Score", "p5Score", "dblScore"].some(poolUsable);
    return !anyPoolUsable;
  })();

  // QH (Quarter Horse) races always require the user to designate their own keys
  // (auto-suggestions are disabled; all non-selected runners are excluded).
  const isQHRace = !!selectedRaceData?.isQH;
  // International/simulcast ("Sim") races behave the same way: no auto-suggest,
  // user must designate keys, and they're excluded from Top Performers.
  const isForeignRace = !!selectedRaceData?.isForeign;
  const requiresOwnKeys = isQHRace || isForeignRace;

  // Standard wagers cap keys at 3; QH/Sim races let the user designate up to the
  // full active field since the selected keys ARE the entire ticket universe.
  const activeRunnerCount = entries.filter((e) => !e.scratched).length;
  const keyCap = requiresOwnKeys ? Math.max(activeRunnerCount, 3) : 3;

  const selectionRequiresKey =
    (!!currentPoolOption?.requiresKey || insufficientDataRequiresKey || requiresOwnKeys) &&
    selectedKeys.length === 0;

  // Steady 60s heartbeat: silently refresh the races list (UP NEXT, current
  // race, post times) while viewing TODAY so a finished race can't sit stale
  // at "POST". One lightweight GetTodaysRaces call/min. The MTP countdown still
  // ticks client-side every 45s for a smooth display.
  useEffect(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (!isToday) return;
    refreshTimerRef.current = setInterval(() => {
      fetchRaces(true); // silent refresh
    }, 60000);
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [isToday]);

  // Fetch races when date changes
  useEffect(() => {
    fetchRaces();
  }, [selectedDate]);

  // Reset dependent selections when date changes
  useEffect(() => {
    setSelectedTrack("");
    setSelectedRace("");
    setSelectedPool("");
    setEntries([]);
    setSelectedKeys([]);
    setResult(null);
  }, [selectedDate]);

  // Clear any prior generate message/result the moment the user changes an input.
  useEffect(() => {
    setError(null);
    setResult(null);
  }, [selectedTrack, selectedRace, selectedPool, selectedKeys, betAmount, straightBetOption, myKeysOnly]);

  // Keep track ref in sync
  useEffect(() => {
    currentTrackRef.current = selectedTrack;
  }, [selectedTrack]);

  // Track the previous track to detect track switches
  const prevTrackRef = useRef(selectedTrack);
  // Flag to indicate UP NEXT navigation (don't clear race)
  const isUpNextNavRef = useRef(false);
  
  // Handle track change: reset dependent state
  useEffect(() => {
    prevTrackRef.current = selectedTrack;
    
    if (!selectedTrack) {
      return;
    }
    
    // Reset dependent state
    setEntries([]);
    setSelectedKeys([]);
    setResult(null);
    
    if (isUpNextNavRef.current) {
      // UP NEXT navigation: the race was set in the same click. The race-change
      // effect won't re-fire if the race NUMBER is unchanged (e.g. switching to a
      // different track that's also on race 3), so load entries here explicitly.
      isUpNextNavRef.current = false;
      if (selectedRace) {
        fetchEntries();
      }
    } else {
      // Normal track change: clear race so auto-select picks the current race
      setSelectedRace("");
    }
    
    console.log('[QuickPick] Track changed to:', selectedTrack);
  }, [selectedTrack]);

  // Auto-select the current race when the track changes (no reordering of races).
  // "Current" = the first race not yet FINAL (i.e. OPEN/POST/upcoming). If the whole
  // card is finished (or a past date), land on race 1 for review. Uses race status
  // from the feed — the old trackData.currentRace was always 0 so this never fired.
  useEffect(() => {
    if (!selectedTrack || loadingRaces || selectedRace) return;
    const trackData = tracks.find((t) => t.code === selectedTrack);
    const trackRaces = trackData?.races || [];
    if (trackRaces.length === 0) return;
    const notDone = (r, i) => r.status !== "FINAL" && r.status !== "CANCELLED" && !r.cancelled
      && !trackRaces.slice(i + 1).some((x) => x.status === "FINAL")
      && !nextCyclingSel(i);
    // Mirror Up Next: clear a stuck race once the next sequential (non-cancelled)
    // race has a feed cycle at <=18 MTP (mtp is null until it actually cycles).
    function nextCyclingSel(i) {
      const nxt = trackRaces.slice(i + 1).find((x) => x.status !== "CANCELLED" && !x.cancelled);
      return !!nxt && nxt.mtp != null && nxt.mtp <= 18;
    }
    const notCancelled = (r) => r.status !== "CANCELLED" && !r.cancelled;
    const current = trackRaces.find(notDone)
      || trackRaces.find(notCancelled)
      || trackRaces[0];
    if (current?.raceNumber != null) {
      setSelectedRace(current.raceNumber.toString());
    }
  }, [selectedTrack, tracks, loadingRaces, selectedRace]);

  // When race changes, fetch entries
  useEffect(() => {
    isUpNextNavRef.current = false; // consumed; avoid leaking into a later dropdown change
    setSelectedKeys([]);
    setResult(null);
    if (selectedTrack && selectedRace) {
      fetchEntries();
    } else {
      setEntries([]);
      // Only clear pool if no track selected (not during track switch transition)
      if (!selectedTrack) {
        setSelectedPool("");
      }
    }
  }, [selectedRace]);

  // Re-apply / validate the remembered pool when pool options load for a race.
  // Keeps the last selected pool sticky race-to-race and across app navigation
  // (BetMaker round-trip) via localStorage. If the current pool isn't offered at
  // this race it's cleared; when a later race offers the remembered pool again it
  // is re-applied automatically.
  const poolOptionsKey = poolOptions.map((o) => o.value).join(",");
  useEffect(() => {
    if (poolOptions.length === 0) return;
    if (selectedPool) {
      if (!poolOptions.some((o) => o.value === selectedPool)) {
        setSelectedPool(""); // not offered here — clear (localStorage keeps the memory)
      }
      return;
    }
    const remembered = localStorage.getItem("quickpick_pool");
    if (remembered && poolOptions.some((o) => o.value === remembered)) {
      setSelectedPool(remembered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolOptionsKey, selectedRace]);

  const fetchRaces = async (silent = false) => {
    console.log('[QuickPick] fetchRaces called, silent:', silent);
    if (!silent) setLoadingRaces(true);
    try {
      const url = `${API}/quickpick/races?date=${selectedDate}`;
      console.log('[QuickPick] Fetching:', url);
      const response = await axios.get(url);
      console.log('[QuickPick] Response:', response.data);
      if (response.data.success) {
        setTracks(response.data.tracks || []);
        setLastRefresh(new Date());
      } else {
        if (!silent) setError(response.data.message || "Failed to fetch races");
      }
    } catch (e) {
      if (!silent) setError("Failed to connect to server");
      console.error('[QuickPick] fetchRaces error:', e);
    } finally {
      if (!silent) setLoadingRaces(false);
    }
  };

  const fetchEntries = async (silent = false) => {
    if (!silent) setLoadingEntries(true);
    try {
      const response = await axios.get(
        `${API}/quickpick/entries?track=${selectedTrack}&race=${selectedRace}&date=${selectedDate}`
      );
      if (response.data.success) {
        setEntries(response.data.entries || []);
        setRacePayouts(response.data.payouts || []);
        setPoolEligibility(response.data.poolEligibility || null);
        setEntriesRefreshedAt(new Date());
      }
    } catch (e) {
      console.error("Failed to fetch entries:", e);
      if (!silent) {
        setEntries([]);
        setRacePayouts([]);
        setPoolEligibility(null);
      }
    } finally {
      if (!silent) setLoadingEntries(false);
    }
  };

  // Adaptive auto-refresh of runner entries (Strong/Fade signals + live odds).
  // Cadence scales with minutes-to-post: MTP >= 6 -> 60s, 3-5 -> 30s, <= 2 -> 15s.
  // Today + a race selected only. Self-scheduling so the cadence tightens as post
  // approaches (and eases off far out — fewer requests than a flat 30s).
  useEffect(() => {
    const trackData = tracks.find((t) => t.code === selectedTrack);
    const raceData = trackData?.races?.find(
      (r) => String(r.raceNumber) === String(selectedRace)
    );
    selectedPostTimeRef.current = raceData?.postTime || null;
  }, [tracks, selectedTrack, selectedRace]);

  useEffect(() => {
    if (entriesTimerRef.current) {
      clearTimeout(entriesTimerRef.current);
      entriesTimerRef.current = null;
    }
    if (!(isToday && selectedTrack && selectedRace)) return;

    const cadence = () => {
      const pt = selectedPostTimeRef.current;
      const mtp = pt ? calculateMTP(pt, selectedDate) : null;
      if (mtp == null) return 60000;
      if (mtp <= 2) return 15000;   // near post
      if (mtp <= 5) return 30000;   // 3-5 MTP
      return 60000;                 // >= 6 MTP
    };
    const tick = () => {
      fetchEntries(true); // silent refresh
      entriesTimerRef.current = setTimeout(tick, cadence());
    };
    entriesTimerRef.current = setTimeout(tick, cadence());

    return () => {
      if (entriesTimerRef.current) {
        clearTimeout(entriesTimerRef.current);
        entriesTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, selectedTrack, selectedRace, selectedDate]);

  const toggleKey = (programNumber) => {
    setSelectedKeys((prev) => {
      if (prev.includes(programNumber)) {
        return prev.filter((k) => k !== programNumber);
      } else {
        if (prev.length >= keyCap) {
          return prev; // Max keys reached
        }
        return [...prev, programNumber];
      }
    });
  };

  const generateTickets = async () => {
    const poolRequired = straightBetOption !== 'win_only' && straightBetOption !== 'wp_only';
    if (!selectedTrack || !selectedRace || (poolRequired && !selectedPool) || !betAmount) {
      setError("Please fill all fields");
      return;
    }
    if ((parseInt(betAmount, 10) || 0) < 5) {
      setError("Minimum bet amount is $5");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setExactaEv(null);

    try {
      const isCombo = poolRequired && selectedPool.includes("+");
      const poolsToRun = isCombo ? selectedPool.split("+") : [selectedPool];

      // Even split of the total bankroll across selected pools (whole dollars)
      const totalBet = parseInt(betAmount);
      const splitEvenly = (total, n) => {
        const base = Math.floor(total / n);
        let rem = total - base * n;
        return Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0));
      };
      const splitAmts = splitEvenly(totalBet, poolsToRun.length);

      const basePayload = {
        clientId: "TEST001",
        track: selectedTrack,
        raceNumber: parseInt(selectedRace),
        raceDate: selectedDate,
        myKeysOnly: myKeysOnly,
      };
      if (selectedKeys.length > 0) {
        basePayload.clientRunners = selectedKeys.join("-");
      }

      if (isCombo) {
        // Run each pool with its split amount. Straight bets (if any) are applied
        // once, on the first pool only, to avoid triple-counting WIN/PLC.
        // Exotic pools: each gets its split amount, no straight bets here.
        const responses = await Promise.all(
          poolsToRun.map((p, i) =>
            axios.post(`${API}/quickpick`, {
              ...basePayload,
              pool: p,
              betAmt: splitAmts[i],
              straightBetOption: "none",
            })
          )
        );
        const pools = poolsToRun.map((p, i) => ({
          code: p,
          betAmt: splitAmts[i],
          data: responses[i].data,
        }));

        // Straight bets (Win/Place) are sized off the FULL bet amount, independent
        // of the pool split. Generated via a separate straight-only call.
        const wantsStraight =
          straightBetOption === "plus_win" || straightBetOption === "plus_wp";
        if (wantsStraight) {
          const straightOpt =
            straightBetOption === "plus_wp" ? "wp_only" : "win_only";
          // Straight bet must use the SAME keys as the exotic pools. With user
          // keys, basePayload already carries them. With auto keys, reuse the
          // first exotic pool's auto-selected keys so the Win/Place bet lands on
          // the same runners shown at the top (not its own independent picks).
          const straightKeys =
            selectedKeys.length > 0
              ? selectedKeys
              : responses[0]?.data?.keysUsed || [];
          try {
            const sResp = await axios.post(`${API}/quickpick`, {
              ...basePayload,
              pool: null,
              betAmt: totalBet,
              straightBetOption: straightOpt,
              clientRunners:
                straightKeys.length > 0 ? straightKeys.join("-") : undefined,
            });
            const sTickets = (sResp.data?.tickets || []).filter(
              (t) => t.combo.startsWith("WIN") || t.combo.startsWith("PLC")
            );
            if (sTickets.length > 0 && pools[0]) {
              pools[0] = {
                ...pools[0],
                data: {
                  ...pools[0].data,
                  tickets: [...(pools[0].data.tickets || []), ...sTickets],
                },
              };
            }
          } catch (se) {
            console.error("Straight bet call failed:", se);
          }
        }

        const failed = pools.filter((p) => !p.data.success);
        if (failed.length === poolsToRun.length) {
          setError(failed[0].data.message || "No tickets created");
          setResult(null);
        } else {
          setResult({ success: true, multi: true, pools });
        }
      } else {
        const response = await axios.post(`${API}/quickpick`, {
          ...basePayload,
          pool: poolRequired ? selectedPool : null,
          betAmt: totalBet,
          straightBetOption: straightBetOption,
        });
        setResult(response.data);
        if (!response.data.success) {
          setError(response.data.message);
        } else if (selectedPool === "EX") {
          // Expected Value for the generated exacta combos (Exacta-Value engine).
          const exotic = (response.data.tickets || []).filter(
            (t) => !t.combo.startsWith("WIN") && !t.combo.startsWith("PLC")
          );
          if (exotic.length > 0 && exactaEvOn) {
            axios
              .post(
                `${API}/exacta-value/ev-for-tickets`,
                {
                  track: selectedTrack,
                  race: parseInt(selectedRace),
                  date: selectedDate,
                  tickets: exotic.map((t) => ({ combo: t.combo, amount: t.amount })),
                },
                { withCredentials: true }
              )
              .then((r) => setExactaEv(r.data))
              .catch(() => setExactaEv(null));
          }
        }
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to generate tickets");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Calculate total wager preview based on straight bet options
  const calculateTotalWager = () => {
    const bet = parseInt(betAmount) || 0;
    if (bet === 0) return { total: 0, breakdown: null, note: null };
    
    // Use user-selected keys, OR if result exists and myKeysOnly is false, use auto-generated keys from result
    let keyCount = selectedKeys.length;
    if (keyCount === 0 && !myKeysOnly && result?.keysUsed?.length > 0) {
      keyCount = result.keysUsed.length;
    }
    
    const isPoolIncluded = straightBetOption === 'none' || straightBetOption === 'plus_win' || straightBetOption === 'plus_wp';
    const hasWin = straightBetOption === 'plus_win' || straightBetOption === 'win_only' || straightBetOption === 'plus_wp' || straightBetOption === 'wp_only';
    const hasPlace = straightBetOption === 'plus_wp' || straightBetOption === 'wp_only';
    
    // Pool name for display
    const poolName = selectedPool || 'Pool';
    
    // If no keys selected and myKeysOnly is checked, no straight bets
    if (keyCount === 0 && myKeysOnly && straightBetOption !== 'none') {
      const poolAmt = isPoolIncluded ? bet : 0;
      return {
        total: poolAmt,
        breakdown: poolAmt > 0 ? `${poolName}: $${poolAmt}` : null,
        note: straightBetOption !== 'none' ? 'Select keys for straight bets' : null
      };
    }
    
    // If no keys and not myKeysOnly and no result yet, we don't know the count
    if (keyCount === 0 && !myKeysOnly && straightBetOption !== 'none') {
      const poolAmt = isPoolIncluded ? bet : 0;
      return {
        total: null,
        breakdown: poolAmt > 0 ? `${poolName}: $${poolAmt}` : null,
        note: 'Straight bets calculated after keys generated'
      };
    }
    
    // Calculate with known key count.
    // Straight bets: the Bet Amount is split EQUALLY across all straight wagers.
    const winCount = hasWin ? keyCount : 0;
    const placeCount = hasPlace ? keyCount : 0;
    const numStraights = winCount + placeCount;
    const perStraight = numStraights > 0 ? Math.max(1, Math.round(bet / numStraights)) : 0;
    const winAmt = winCount * perStraight;
    const placeAmt = placeCount * perStraight;
    const poolAmt = isPoolIncluded ? bet : 0;
    const total = winAmt + placeAmt + poolAmt;

    let parts = [];
    if (winAmt > 0) parts.push(`Win: $${winAmt} (${winCount}×$${perStraight})`);
    if (placeAmt > 0) parts.push(`Place: $${placeAmt} (${placeCount}×$${perStraight})`);
    if (poolAmt > 0) parts.push(`${poolName}: $${poolAmt}`);

    return {
      total,
      breakdown: parts.join(' + '),
      note: null
    };
  };

  const wagerPreview = calculateTotalWager();

  // IntelliTix download inputs snapshot — only when the user designated their own
  // keys (pure Auto-Generate records no inputs). Mirrors the iPro "Inputs used"
  // panel the admin Downloads view renders (keys marked ★).
  const ownKeyInputs = () => {
    if (!selectedKeys || selectedKeys.length === 0) return null;
    const keySel = {};
    selectedKeys.forEach((k) => { keySel[String(k)] = true; });
    return { mode: 'IntelliTix', pools: selectedPool ? selectedPool.split('+') : [], keySel };
  };

  // Bet amount must be a whole number >= $5
  const betAmountNum = parseInt(betAmount, 10) || 0;
  const betAmountValid = betAmountNum >= 5;

  // Late-Indicator visual suppression (UI ONLY — does NOT affect key selection or
  // ticket construction). When a flag type appears on >2 active runners the signal
  // is too noisy, so hide that type's row rings. Evaluated independently per type.
  // NOTE (2026-07-23): Fade (dont-use) suppression removed per owner — always show fade.
  const activeEntries = entries.filter((e) => !e.scratched);
  const suppressMU = activeEntries.filter((e) => e.mustUse).length > 2;
  const suppressDU = false;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header with User Menu */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-emerald-400">
              IntelliTix<sup>®</sup>
            </h1>
            <p className="text-slate-400 mt-1">
              Intelligent ticket generation
            </p>
            <button
              onClick={() => window.open('https://youtu.be/5-zDUXpb2FU', '_blank', 'noopener')}
              className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition font-medium"
              data-testid="intellitix-video-link"
              title="Watch the IntelliTix overview video"
            >
              <PlayCircle className="h-4 w-4" />
              <span>Watch overview</span>
            </button>
          </div>
          <div className="hidden md:flex flex-1 justify-center self-center">
            <TrialBadge theme="dark" />
          </div>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            {/* User Menu */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <span
                  aria-hidden="true"
                  className="absolute -inset-1 rounded-full bg-emerald-500/40 blur-md animate-pulse pointer-events-none"
                />
                <button
                  onClick={() => window.open('/user-guide.html', '_blank', 'noopener')}
                  className="relative flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-lg shadow-emerald-500/40 ring-2 ring-emerald-300/50 transition-transform hover:scale-105"
                  data-testid="user-guide-btn"
                  title="New here? Open the IntelliTix User Guide"
                >
                  <BookOpen className="h-4 w-4" />
                  <span>User Guide</span>
                </button>
              </div>
              <button
                onClick={() => window.open('/file-upload-guide.html', '_blank', 'noopener')}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-lg shadow-emerald-600/30 border border-emerald-400/40 transition-transform hover:scale-105"
                data-testid="file-upload-guide-btn"
                title="How to upload your tickets to your ADW"
              >
                <Upload className="h-4 w-4" />
                <span>File Upload Guide</span>
              </button>
              {hasBetmaker() && (
                <Link 
                  to="/betmaker" 
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm shadow-md shadow-amber-500/30 border border-amber-400/40 transition-transform hover:scale-105"
                  data-testid="betmaker-link"
                  title="Switch to BetMaker"
                >
                  <ArrowLeftRight className="w-4 h-4" />
                  <span>IntelliTix<sup>Pro</sup></span>
                </Link>
              )}
              {hasExactaValue() && (
                <Link
                  to="/exacta-value"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-md shadow-emerald-500/30 border border-emerald-400/40 transition-transform hover:scale-105"
                  data-testid="exacta-value-link"
                  title="Exacta Value — market-mispriced combinations"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span>Exacta Value</span>
                </Link>
              )}
              {user?.role === 'admin' && (
                <a
                  href="/pixticker?clientId=TEST001"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-lime-400 hover:text-lime-300 transition font-semibold"
                  data-testid="pixticker-link"
                >
                  PixTicker
                </a>
              )}
              {user?.is_affiliate && (
                <Link 
                  to="/affiliate" 
                  className="text-sm text-amber-400 hover:text-amber-300 transition"
                  data-testid="affiliate-dashboard-link"
                >
                  Affiliate Dashboard
                </Link>
              )}
              <TriBetLink />
              {user?.role === 'admin' && (
                <Link 
                  to="/admin" 
                  className="text-sm text-purple-400 hover:text-purple-300 transition"
                  data-testid="admin-link"
                >
                  Admin
                </Link>
              )}
              <AppMenu user={user} onLogout={logout} showPreferences onOpenPreferences={() => setPrefsOpen(true)} theme="dark" />
              <BetMakerPreferences open={prefsOpen} onOpenChange={setPrefsOpen} prefs={prefs} betmakerEnabled={betmakerEnabled} onSave={savePrefs} />
            </div>
          </div>
        </div>

        {/* UP NEXT - Quick navigation to current races */}
        {isToday && upNextTracks.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-3" key={`upnext-${mtpTick}`}>
            <div className="flex items-center gap-3">
              <span className="text-xs font-extrabold text-slate-400 uppercase tracking-wider whitespace-nowrap">
                UP NEXT
              </span>
              <div className="flex gap-2 overflow-x-auto pb-1 min-w-0 flex-1 scrollbar-thin scrollbar-thumb-slate-700">
                {upNextTracks.map((track) => {
                  const mtp = formatMTP(track.postTime, selectedDate);
                  const tri = triPlays[`${track.code}|${track.currentRace}`];
                  return (
                    <button
                      key={track.code}
                      onClick={() => {
                        if (tri) { optInTri(track, tri); return; }
                        isUpNextNavRef.current = true; // Flag to prevent race clearing
                        setSelectedTrack(track.code);
                        setSelectedRace(track.currentRace.toString());
                      }}
                      className={`relative flex-shrink-0 px-3 py-1.5 rounded text-sm font-bold transition-colors ${
                        tri
                          ? "bg-fuchsia-600 text-white ring-2 ring-fuchsia-300 animate-pulse"
                          : selectedTrack === track.code
                          ? "bg-emerald-600 text-white"
                          : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                      }`}
                      data-testid={tri ? `triplay-${track.code}` : `upnext-${track.code}`}
                      title={tri ? `+ROI Trifecta — opt in to build in iPro` : (track.name || track.code)}
                    >
                      {track.code} R{track.currentRace}{" "}
                      <span className={mtp.isUrgent ? "text-red-300 font-extrabold" : ""}>
                        {mtp.text}
                      </span>
                      {tri && (
                        <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-extrabold uppercase">
                          <Zap className="w-3 h-3" /> +ROI Tri
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr_1.2fr] gap-4">
          {/* Input Panel */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-emerald-400 text-lg whitespace-nowrap">Race Selection</CardTitle>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="h-8 bg-slate-800 border-slate-700 text-slate-100 text-sm w-36 [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:hover:opacity-100"
                    data-testid="date-picker"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchRaces()}
                    disabled={loadingRaces}
                    className="h-7 px-2 text-slate-400 hover:text-emerald-400"
                    data-testid="refresh-races-btn"
                    title={lastRefresh ? `Last refresh: ${lastRefresh.toLocaleTimeString()}` : 'Refresh races'}
                  >
                    <RefreshCw className={`h-4 w-4 ${loadingRaces ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {loadingRaces ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
                  <span className="ml-2 text-slate-400">Loading...</span>
                </div>
              ) : (
                <>
                  {(() => {
                    const tri = triDay.byKey[`${selectedTrack}|${selectedRace}`];
                    if (!tri) return null;
                    return (
                      <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-fuchsia-500/60 bg-fuchsia-600/15 px-3 py-2" data-testid="tri-race-banner">
                        <div className="flex items-center gap-2 text-fuchsia-300">
                          <Zap className="w-4 h-4" />
                          <span className="text-sm font-bold">+ROI Trifecta</span>
                        </div>
                        <button
                          onClick={() => optInTriRace(selectedTrack, parseInt(selectedRace), tri)}
                          className="shrink-0 px-3 py-1.5 rounded-md text-xs font-bold bg-fuchsia-600 text-white hover:bg-fuchsia-500 transition"
                          data-testid="tri-race-optin-btn"
                        >
                          Build in iPro →
                        </button>
                      </div>
                    );
                  })()}
                  {/* Track Select */}
                  <div className="space-y-1">
                    <Label className="text-slate-300 text-sm">Track</Label>
                    <Select
                      value={selectedTrack}
                      onValueChange={setSelectedTrack}
                    >
                      <SelectTrigger
                        className="bg-slate-800 border-slate-700 text-slate-100 h-9"
                        data-testid="track-select"
                      >
                        <SelectValue placeholder={(loadingRaces && tracks.length === 0) ? "Loading tracks..." : "Select track"} />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700 max-h-[60vh]">
                        {tracks.length === 0 ? (
                          <div
                            className="px-2 py-2 text-sm text-slate-400"
                            data-testid="track-select-empty"
                          >
                            {loadingRaces ? "Loading tracks…" : "No tracks available — tap refresh"}
                          </div>
                        ) : (
                          [...tracks.filter((t) => !t.finished), ...tracks.filter((t) => t.finished)].map((track) => (
                            <SelectItem
                              key={track.code}
                              value={track.code}
                              data-testid={`track-option-${track.code}`}
                              className={`focus:bg-emerald-600 focus:text-white ${track.finished ? "text-slate-500" : "text-slate-100"}`}
                            >
                              <span className="flex items-center gap-2">
                                <span>{track.code} - {track.name}</span>
                                {track.finished && (
                                  <span
                                    className="text-[10px] font-semibold tracking-wide px-1.5 py-0.5 rounded bg-slate-700 text-slate-400"
                                    data-testid={`track-fin-tag-${track.code}`}
                                  >
                                    FIN
                                  </span>
                                )}
                              </span>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Race Select with Navigation Arrows */}
                  <div className="space-y-1">
                    <Label className="text-slate-300 text-sm">Race</Label>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          const currentIdx = races.findIndex(r => r.raceNumber.toString() === selectedRace);
                          if (currentIdx > 0) {
                            setSelectedRace(races[currentIdx - 1].raceNumber.toString());
                          }
                        }}
                        disabled={!selectedTrack || !selectedRace || races.findIndex(r => r.raceNumber.toString() === selectedRace) === 0}
                        className="p-2 bg-slate-800 border border-slate-700 rounded-md text-slate-300 hover:bg-slate-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition shrink-0"
                        data-testid="race-prev-btn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
                      </button>
                      <Select
                        value={selectedRace}
                        onValueChange={setSelectedRace}
                        disabled={!selectedTrack}
                      >
                        <SelectTrigger
                          className="bg-slate-800 border-slate-700 text-slate-100 h-9 flex-1"
                          data-testid="race-select"
                        >
                          <SelectValue placeholder="Race" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {races.map((race) => {
                            const pt = formatLocalTime(race.postTime, selectedDate);
                            const isCancelled = race.cancelled || race.status === "CANCELLED";
                            const isFinal = race.status === "FINAL" && !isCancelled;
                            return (
                              <SelectItem
                                key={race.raceNumber}
                                value={race.raceNumber.toString()}
                                disabled={isCancelled}
                                data-testid={`race-option-${race.raceNumber}`}
                                className={`focus:bg-emerald-600 focus:text-white ${isCancelled ? "text-slate-600 line-through opacity-60" : isFinal ? "text-slate-500" : "text-slate-100"}`}
                              >
                                <span className="flex items-center gap-2">
                                  <span>R{race.raceNumber}{pt ? ` - ${pt}` : ""}{race.isQH ? " (QH)" : ""}{race.isForeign ? " (Sim)" : ""}</span>
                                  {triDay.byKey[`${selectedTrack}|${race.raceNumber}`] && (
                                    <span className="text-[10px] font-extrabold tracking-wide px-1.5 py-0.5 rounded bg-fuchsia-600 text-white" data-testid={`race-tri-tag-${race.raceNumber}`}>+TRI</span>
                                  )}
                                  {isCancelled ? (
                                    <span
                                      className="text-[10px] italic font-semibold text-red-500 no-underline"
                                      data-testid={`race-cancelled-${race.raceNumber}`}
                                    >
                                      Cancelled
                                    </span>
                                  ) : isFinal && (
                                    <span
                                      className="text-[10px] italic font-medium text-amber-500/80"
                                      data-testid={`race-official-${race.raceNumber}`}
                                    >
                                      Official
                                    </span>
                                  )}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                      <button
                        onClick={() => {
                          const currentIdx = races.findIndex(r => r.raceNumber.toString() === selectedRace);
                          if (currentIdx < races.length - 1) {
                            setSelectedRace(races[currentIdx + 1].raceNumber.toString());
                          }
                        }}
                        disabled={!selectedTrack || !selectedRace || races.findIndex(r => r.raceNumber.toString() === selectedRace) === races.length - 1}
                        className="p-2 bg-slate-800 border border-slate-700 rounded-md text-slate-300 hover:bg-slate-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition shrink-0"
                        data-testid="race-next-btn"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                      </button>
                    </div>
                  </div>

                  {/* Pool Select */}
                  <div className="space-y-1">
                    <Label className="text-sm text-slate-300">Pool</Label>
                    {(straightBetOption === 'win_only' || straightBetOption === 'wp_only') ? (
                      <div
                        className="flex h-9 items-center rounded-md border border-emerald-500/80 bg-emerald-500/15 px-3"
                        data-testid="pool-straight-only-badge"
                      >
                        <span className="text-xs font-bold uppercase tracking-wide text-emerald-300">
                          Straight Bets Only — {straightBetOption === 'wp_only' ? 'Win/Place' : 'Win'}
                        </span>
                      </div>
                    ) : (
                      <Select
                        value={selectedPool}
                        onValueChange={setSelectedPool}
                        disabled={!selectedRace}
                      >
                        <SelectTrigger
                          className="bg-slate-800 border-slate-700 text-slate-100 h-9"
                          data-testid="pool-select"
                        >
                          <SelectValue placeholder="Select pool" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {poolOptions.map((opt) => (
                            <SelectItem
                              key={opt.value}
                              value={opt.value}
                              className="text-slate-100 focus:bg-emerald-600 focus:text-white"
                              data-testid={`pool-option-${opt.value}`}
                            >
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Straight Bet Options */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-slate-300 text-sm">Straight Bet Options</Label>
                      <button
                        type="button"
                        onClick={() => setShowStraightBetInfo(!showStraightBetInfo)}
                        className="text-base text-yellow-400 hover:text-yellow-300 font-semibold leading-none transition"
                        data-testid="straight-bet-info-toggle"
                      >
                        [?]
                      </button>
                    </div>
                    
                    {/* Collapsible Info Box */}
                    {showStraightBetInfo && (
                      <div className="px-3 py-2 bg-slate-800/80 border border-slate-600 rounded-lg text-xs text-slate-200 space-y-2">
                        <p><strong>Straight Bet Options</strong> let you add Win and/or Place wagers on key runners—either alongside your exotic pool or by themselves.</p>
                        <p><strong>Example:</strong> Bet Amount $40 + Win/Place + Exacta with 2 keys → <span className="text-emerald-400">$80 total</span>: $40 to the Exacta, and $40 split evenly across the 4 straight bets ($10 each: Win ×2, Place ×2).</p>
                        <p>Check <strong>&quot;My Keys Only&quot;</strong> to limit straight bets to your manually selected keys, excluding any auto-generated keys.</p>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3">
                      <Select
                        value={straightBetOption}
                        onValueChange={setStraightBetOption}
                      >
                        <SelectTrigger
                          className="bg-slate-800 border-slate-700 text-slate-100 h-9 w-40"
                          data-testid="straight-bet-select"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          <SelectItem value="none" className="text-slate-100 focus:bg-emerald-600 focus:text-white">
                            None
                          </SelectItem>
                          <SelectItem value="plus_win" className="text-slate-100 focus:bg-emerald-600 focus:text-white">
                            + Win
                          </SelectItem>
                          <SelectItem value="plus_wp" className="text-slate-100 focus:bg-emerald-600 focus:text-white">
                            + Win/Place
                          </SelectItem>
                          <SelectItem value="win_only" className="text-slate-100 focus:bg-emerald-600 focus:text-white">
                            Win Only
                          </SelectItem>
                          <SelectItem value="wp_only" className="text-slate-100 focus:bg-emerald-600 focus:text-white">
                            Win/Place Only
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={myKeysOnly}
                          onChange={(e) => setMyKeysOnly(e.target.checked)}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-emerald-600 focus:ring-emerald-500"
                          data-testid="my-keys-only-checkbox"
                        />
                        <span className="text-slate-300 text-sm">My Keys Only</span>
                      </label>
                    </div>
                  </div>

                  {/* Bet Amount */}
                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm">
                      Bet Amount{" "}
                      <span className="text-slate-500 text-xs">
                        (Full amount is bet on <em>both</em> Exotics and Straight wagers. The minimum stake for a single EX combination is $2.)
                      </span>
                    </Label>
                    <div className="flex gap-2">
                      <div className="flex flex-col items-start">
                        <Input
                          type="text"
                          inputMode="numeric"
                          value={betAmount}
                          onChange={(e) => setBetAmount(e.target.value.replace(/[^0-9]/g, ""))}
                          onFocus={(e) => e.target.select()}
                          onClick={(e) => e.target.select()}
                          className={`text-slate-100 h-9 w-20 bg-amber-400/15 ${
                            betAmount && !betAmountValid ? "border-red-500 focus-visible:ring-red-500" : "border-amber-400/40 focus-visible:ring-amber-400/50"
                          }`}
                          placeholder="20"
                          data-testid="bet-amount-input"
                        />
                        <span className="text-xs text-slate-400 mt-1">Custom</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {[20, 30, 40, 50, 75, 100, 150, 200, 250, 300, 500].map((amt) => (
                          <Button
                            key={amt}
                            variant="outline"
                            size="sm"
                            onClick={() => setBetAmount(amt.toString())}
                            className={`h-9 px-2 text-xs border-slate-600 ${
                              betAmount === amt.toString() 
                                ? "bg-emerald-600 text-white border-emerald-600" 
                                : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                            }`}
                          >
                            ${amt}
                          </Button>
                        ))}
                      </div>
                    </div>
                    {betAmount && !betAmountValid && (
                      <p className="text-xs text-red-400" data-testid="bet-amount-error">
                        Minimum bet amount is $5
                      </p>
                    )}
                  </div>

                  {/* Total Wager Preview */}
                  {straightBetOption !== 'none' && betAmount && (
                    <div className="p-2 bg-slate-800/50 border border-slate-700 rounded">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400 text-sm">Total Wager:</span>
                        {wagerPreview.total !== null ? (
                          <span className="text-emerald-400 font-bold">${wagerPreview.total}</span>
                        ) : (
                          <span className="text-slate-400 text-sm italic">TBD</span>
                        )}
                      </div>
                      {wagerPreview.breakdown && (
                        <div className="text-xs text-slate-500 mt-1">
                          {wagerPreview.breakdown}
                        </div>
                      )}
                      {wagerPreview.note && (
                        <div className="text-xs text-amber-400/80 mt-1 italic">
                          {wagerPreview.note}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Selected Keys Display */}
                  {selectedKeys.length > 0 && (
                    <div className="p-2 bg-slate-800 rounded space-y-1">
                      <div>
                        <span className="text-slate-400 text-sm">Your Keys: </span>
                        {selectedKeys.map((k) => (
                          <Badge key={k} className="mr-1 bg-amber-600 text-white">
                            #{k}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-amber-400">
                        <Info className="h-3 w-3" />
                        {selectedKeys.length === 1 
                          ? "Your Key Must Run 1st or 2nd"
                          : "One of Your Keys Must Win"
                        }
                      </div>
                    </div>
                  )}

                  {/* Generate Button */}
                  <Button
                    onClick={generateTickets}
                    disabled={loading || !selectedTrack || !selectedRace || (!selectedPool && straightBetOption !== 'win_only' && straightBetOption !== 'wp_only') || selectionRequiresKey || !betAmountValid}
                    className={`w-full text-white ${selectionRequiresKey ? "bg-amber-700 hover:bg-amber-700 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"}`}
                    data-testid="generate-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : selectionRequiresKey ? (
                      <span className="flex items-center justify-center gap-2">
                        <Key className="h-4 w-4" />
                        This Wager Requires You to Designate Keys
                      </span>
                    ) : selectedKeys.length > 0 ? (
                      `Generate with Keys (${selectedKeys.join(",")})`
                    ) : (
                      "Auto Generate"
                    )}
                  </Button>

                  <p className="text-sm text-yellow-400 mt-2 text-center" data-testid="late-signal-notice">
                    Strong/Fade signals can change the runner selections. For Best Results - Wait for "Proj" Odds to Populate and/or Late Signals @ 1MTP.
                  </p>

                  {/* Error Display */}
                  {error && (
                    <div className="p-2 bg-red-900/50 border border-red-700 rounded text-red-300 text-sm">
                      {error}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Runners Panel */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-emerald-400 text-lg">
                Runners {selectedKeys.length > 0 && `(${selectedKeys.length} keys selected)`}
                {selectedRaceData?.status === "FINAL" && (
                  <span
                    className="ml-2 align-middle text-[10px] italic font-semibold tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30"
                    data-testid="runners-official-badge"
                  >
                    OFFICIAL
                  </span>
                )}
                {isQHRace && (
                  <span
                    className="ml-2 align-middle text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 border border-orange-500/40"
                    data-testid="runners-qh-badge"
                    title="Quarter Horse race — designate your own keys; all non-selected runners are excluded."
                  >
                    QH
                  </span>
                )}
                {isForeignRace && (
                  <span
                    className="ml-2 align-middle text-[10px] font-bold tracking-wide px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-400 border border-sky-500/40"
                    data-testid="runners-sim-badge"
                    title="International / simulcast race — designate your own keys; all non-selected runners are excluded, and it's omitted from Top Performers."
                  >
                    SIM
                  </span>
                )}
                {isToday && entriesRefreshedAt && selectedRaceData?.status !== "FINAL" && (
                  <span
                    className="ml-2 text-xs font-normal text-slate-500"
                    data-testid="entries-refresh-indicator"
                    title="Live odds auto-refresh every 60s"
                  >
                    · live odds updated {formatAgo(entriesRefreshedAt)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingEntries ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
                </div>
              ) : entries.length > 0 ? (
                <div className="space-y-1">
                  <Table className="w-full table-fixed sm:table-auto">
                    <TableHeader>
                      <TableRow className="border-slate-700 bg-slate-800">
                        <TableHead className="text-slate-300 w-9 sm:w-10 px-1 sm:px-2">Key</TableHead>
                        <TableHead className="text-slate-300 w-8 sm:w-12 px-1 sm:px-2">#</TableHead>
                        <TableHead className="text-slate-300 w-14 sm:w-auto">Name</TableHead>
                        <TableHead className="text-slate-300 text-right w-12 sm:w-14 px-1 sm:px-1.5">
                          <span className="inline-flex items-center justify-end gap-1">
                            Live
                            <TooltipProvider delayDuration={100}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="text-base text-yellow-400 hover:text-yellow-300 font-semibold leading-none transition cursor-help"
                                    data-testid="odds-info-tooltip"
                                  >
                                    [?]
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs bg-slate-900 text-slate-100 border border-slate-600 text-xs leading-relaxed">
                                  Live Odds are only used in ticket construction when Projected Odds are not available (n/a). When Live Odds are not available, the Morning Line Odds are used. IntelliTix uses Projected Odds, which allows you to create your tickets with confidence — even @ 20 MTP.
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </span>
                        </TableHead>
                        <TableHead className="text-slate-300 text-right w-12 sm:w-14 px-1 sm:px-1.5">Proj</TableHead>
                        <TableHead className="text-slate-400 text-right w-11 sm:w-12 px-1 sm:px-1.5">ML</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => {
                        const isScratched = entry.scratched;
                        const isSelected = selectedKeys.includes(entry.programNumber);
                        const isMustUse = entry.mustUse && !isScratched;
                        const isDontUse = entry.dontUse && !isScratched;
                        const handleToggle = () => {
                          if (!isScratched) {
                            toggleKey(entry.programNumber);
                          }
                        };
                        
                        return (
                          <TableRow
                            key={entry.programNumber}
                            data-testid={`runner-row-${entry.programNumber}`}
                            data-flag={isMustUse ? "must-use" : isDontUse ? "dont-use" : "none"}
                            className={`border-slate-700 ${
                              isScratched 
                                ? "opacity-40 cursor-not-allowed" 
                                : `cursor-pointer hover:bg-slate-800 ${isSelected ? "bg-amber-900/30" : ""} ${isDontUse && !suppressDU ? "ring-2 ring-inset ring-amber-500/80" : entry.overbetFav ? "ring-2 ring-inset ring-violet-500/80" : isMustUse && !suppressMU ? "ring-2 ring-inset ring-emerald-500/80" : ""}`
                            }`}
                            onClick={handleToggle}
                          >
                            <TableCell className="p-1 sm:p-2" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={isSelected}
                                disabled={isScratched}
                                onCheckedChange={handleToggle}
                                className="border-slate-600 data-[state=checked]:bg-amber-600"
                              />
                            </TableCell>
                            <TableCell className={`font-bold p-1 sm:p-2 ${isScratched ? "line-through" : ""}`}>
                              <span className={pnClass(entry.programNumber)} data-testid={`runner-pn-${entry.programNumber}`}>
                                {entry.programNumber}
                              </span>
                            </TableCell>
                            <TableCell className={`px-1 py-2 sm:p-2 truncate sm:overflow-visible sm:whitespace-normal ${isScratched ? "text-slate-500" : "text-slate-300"}`}>
                              {Array.isArray(entry.coupledMembers) && entry.coupledMembers.length > 1 ? (
                                entry.coupledMembers.map((m, idx) => (
                                  <span key={m.number}>
                                    {idx > 0 && <span className="text-slate-500"> / </span>}
                                    <span
                                      className={m.scratched ? "line-through text-slate-500" : ""}
                                      data-testid={`runner-member-${entry.programNumber}-${m.number}`}
                                    >
                                      {m.name}
                                    </span>
                                  </span>
                                ))
                              ) : (
                                <span title={[entry.jockey, entry.trainer].some(Boolean) ? `${entry.jockey || '—'} | ${entry.trainer || '—'}` : undefined}
                                  data-testid={`runner-name-${entry.programNumber}`}>{entry.horseName || `Horse ${entry.programNumber}`}</span>
                              )}
                              {isScratched && <span className="ml-2 text-red-400 text-xs">(SCR)</span>}
                              {isMustUse && !suppressMU && IS_SAFARI && (
                                <span
                                  className="ml-2 inline-flex items-center align-middle whitespace-nowrap rounded border border-emerald-500/80 bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-300"
                                  data-testid={`signal-strong-${entry.programNumber}`}
                                >
                                  Strong
                                </span>
                              )}
                              {isDontUse && !suppressDU && IS_SAFARI && (
                                <span
                                  className="ml-2 inline-flex items-center align-middle whitespace-nowrap rounded border border-amber-500/80 bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-300"
                                  data-testid={`signal-fade-${entry.programNumber}`}
                                >
                                  Exacta Fade
                                </span>
                              )}
                              {SHOW_OVERBET_FAV && entry.overbetFav && !isScratched && (
                                <span
                                  title={overbetTip(activeRunnerCount)}
                                  data-testid={`signal-overbet-${entry.programNumber}`}
                                  className="ml-1 inline-flex align-middle cursor-help"
                                >
                                  <Info className="w-3.5 h-3.5 text-violet-400" />
                                </span>
                              )}
                            </TableCell>
                            <TableCell className={`text-right px-1 sm:px-1.5 py-2 ${isScratched ? "text-slate-500" : "text-slate-100"}`}>
                              {entry.xmlOdds > 0 ? entry.xmlOdds.toFixed(1) : "-"}
                            </TableCell>
                            <TableCell className={`text-right px-1 sm:px-1.5 py-2 ${isScratched ? "text-slate-500" : "text-emerald-400"}`}>
                              {entry.projOdds > 0 ? entry.projOdds.toFixed(1) : "n/a"}
                            </TableCell>
                            <TableCell className={`text-right px-1 sm:px-1.5 py-2 text-xs ${isScratched ? "text-slate-600" : "text-slate-400"}`} data-testid={`runner-ml-${entry.programNumber}`}>
                              {formatML(entry.mlOdds)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <p className="text-sm text-slate-300 mt-2 font-bold">
                    Click row or checkbox to select keys (max {keyCap})
                  </p>
                  {/* Late Signal legend — Strong and Fade with @1MTP timing note */}
                  <div
                    className="flex flex-nowrap items-center gap-2 mt-2"
                    data-testid="runner-flag-legend"
                  >
                    <span className="text-xs font-semibold text-yellow-400 uppercase tracking-wide">1 MTP Odds Signal:</span>
                    <span className="inline-flex items-center whitespace-nowrap rounded border border-emerald-500/80 bg-emerald-500/15 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-emerald-300">
                      Strong
                    </span>
                    <span className="inline-flex items-center whitespace-nowrap rounded border border-amber-500/80 bg-amber-500/15 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-amber-300">
                      Exacta Fade
                    </span>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-violet-500/80 bg-violet-500/15 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-violet-300">
                      Overbet <Info className="w-3 h-3" />
                    </span>
                  </div>

                  {/* Race payoffs: shown once the race is official (payouts posted),
                      whether or not a play has been generated — mirrors BetMaker. */}
                  {(() => {
                    const rp = result
                      ? (result.multi
                          ? (result.pools?.find((p) => (p.data?.payouts || []).length > 0)?.data?.payouts || [])
                          : (result.payouts || []))
                      : racePayouts;
                    return rp.length > 0 ? (
                      <RacePayoffs payouts={rp} entries={entries} />
                    ) : null;
                  })()}
                </div>
              ) : (
                <div className="text-slate-500 text-center py-8">
                  Select a race to see runners
                </div>
              )}
            </CardContent>
          </Card>

          {/* Results Panel */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-3">
              <CardTitle className="text-emerald-400 text-lg">
                Results
                {selectedRaceData?.status === "FINAL" && (
                  <span
                    className="ml-2 align-middle text-[10px] italic font-semibold tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30"
                    data-testid="results-official-badge"
                  >
                    OFFICIAL
                  </span>
                )}
                {result?.payouts?.length > 0 && (() => {
                  // Get finish order from payouts, preferring SPR > TRI > EX > WIN
                  const poolOrder = ['spr', 'superfecta', 'tri', 'trifecta', 'ex', 'exacta', 'win'];
                  let finishOrder = null;
                  for (const poolName of poolOrder) {
                    const payout = result.payouts.find(p => p.pool?.toLowerCase() === poolName);
                    if (payout?.winners) {
                      finishOrder = payout.winners;
                      break;
                    }
                  }
                  return finishOrder ? (
                    <span className="text-slate-400 font-normal text-sm ml-2">({finishOrder})</span>
                  ) : null;
                })()}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {result?.multi ? (
                <div className="space-y-3">
                  <MultiPoolResults pools={result.pools} />

                  {/* Download Links (combined across all pools in the combo) */}
                  <div className="flex gap-2 justify-end mt-2">
                    <AdwButton track={selectedTrack} race={selectedRace} date={selectedDate}
                      getPools={() => (result.pools || []).filter((p) => p.data?.success && (p.data.tickets || []).length)
                        .reduce((a, p) => { a[p.code] = p.data.tickets.map((t) => ({ combo: t.combo, amount: t.amount })); return a; }, {})}
                      getKeys={() => [...new Set((result.pools || []).flatMap((p) => p.data?.keysUsed || []))]}
                      className="h-7 px-2 text-xs border-rose-500 bg-slate-800 text-rose-300 hover:bg-slate-700" />
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="combo-download-amwager-btn"
                      onClick={() => {
                        const amCode = selectedTrackData?.amcode || selectedTrack;
                        const content = (result.pools || [])
                          .filter((p) => p.data?.success && (p.data.tickets || []).length > 0)
                          .map((p) => generateAmwagerFormat(amCode, selectedRace, p.code, p.data.tickets, selectedDate, wheelFor(p.code, p.data.tickets, p.data), p.data.poolMinimum, p.data.ticketMin))
                          .join("\n");
                        const poolTag = (selectedPool || "combo").replace(/\+/g, "-");
                        downloadFile(content, `amwager${selectedDate}_${amCode}_r${selectedRace}_${poolTag}.txt`);
                        const totalTix = (result.pools || []).reduce((s, p) => s + ((p.data?.tickets || []).length), 0);
                        const totalWag = (result.pools || []).reduce((s, p) => s + (p.data?.totalWager || 0), 0);
                        const allTix = (result.pools || []).flatMap((p) => (p.data?.tickets || []).map((t) => ({ pool: p.code, combo: t.combo, amount: t.amount })));
                        logDownload(selectedTrack, selectedRace, selectedPool, "amwager", totalTix, totalWag, selectedDate, allTix, ownKeyInputs());
                      }}
                      className="h-7 px-2 text-xs border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Amwager
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid="combo-download-xpressbet-btn"
                      onClick={() => {
                        const xbCode = selectedTrackData?.xbcode || selectedTrack;
                        const content = (result.pools || [])
                          .filter((p) => p.data?.success && (p.data.tickets || []).length > 0)
                          .map((p) => generateXpressBetFormat(xbCode, selectedRace, p.code, p.data.tickets, selectedDate, wheelFor(p.code, p.data.tickets, p.data), p.data.poolMinimum, p.data.ticketMin))
                          .join("\n");
                        const poolTag = (selectedPool || "combo").replace(/\+/g, "-");
                        downloadFile(content, `xpressbet${selectedDate}_${xbCode}_r${selectedRace}_${poolTag}.txt`);
                        const totalTix = (result.pools || []).reduce((s, p) => s + ((p.data?.tickets || []).length), 0);
                        const totalWag = (result.pools || []).reduce((s, p) => s + (p.data?.totalWager || 0), 0);
                        const allTix = (result.pools || []).flatMap((p) => (p.data?.tickets || []).map((t) => ({ pool: p.code, combo: t.combo, amount: t.amount })));
                        logDownload(selectedTrack, selectedRace, selectedPool, "xpressbet", totalTix, totalWag, selectedDate, allTix, ownKeyInputs());
                      }}
                      className="h-7 px-2 text-xs border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      XpressBet
                    </Button>
                  </div>
                </div>
              ) : result?.success ? (
                <div className="space-y-3">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-slate-800 p-2 rounded">
                      <div className="text-slate-400 text-xs">Field Size</div>
                      <div className="text-lg font-bold text-slate-100">
                        {result.debug?.fieldSize}
                      </div>
                      {selectedPool === "EX" && exactaEv?.available && (
                        <div className="mt-1 pt-1 border-t border-slate-700/60" data-testid="qp-exacta-ev">
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-slate-400">Exp. ROI</span>
                            <span
                              className={`font-bold ${exactaEv.expected_roi_pct == null ? "text-slate-400" : exactaEv.expected_roi_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}
                              data-testid="qp-exacta-ev-roi"
                            >
                              {exactaEv.expected_roi_pct == null ? "—" : `${exactaEv.expected_roi_pct > 0 ? "+" : ""}${exactaEv.expected_roi_pct}%`}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="text-slate-400">Exp. Return</span>
                            <span className="text-slate-200" data-testid="qp-exacta-ev-return">
                              ${exactaEv.expected_return?.toFixed(2)}
                            </span>
                          </div>
                          {exactaEv.unpriced > 0 && (
                            <div className="text-[10px] text-amber-400/80 italic mt-0.5">
                              {exactaEv.unpriced} combo{exactaEv.unpriced > 1 ? "s" : ""} unpriced (excluded from EV)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="bg-slate-800 p-2 rounded">
                      <div className="text-slate-400 text-xs">Total Wager</div>
                      <div className="text-lg font-bold text-emerald-400">
                        ${result.totalWager?.toFixed(2)}
                      </div>
                      {["EX", "TRI", "SPR"].includes(selectedPool) && (() => {
                        const exotic = (result.tickets || []).filter((t) => !t.combo.startsWith("WIN,") && !t.combo.startsWith("PLC,"));
                        const play = actualPlayTotal(exotic, selectedPool, result.poolMinimum, result.ticketMin, wheelFor(selectedPool, exotic, result));
                        return play.total > (exotic.reduce((s, t) => s + t.amount, 0)) + 0.001 ? (
                          <div className="text-[11px] text-amber-400 font-medium mt-0.5" data-testid="qp-actual-play-total">
                            Plays ${play.total.toFixed(2)} at window ({singleMinNote(selectedPool, play.bumped, result.ticketMin)})
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>

                  {/* Keys & Runners */}
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center flex-wrap gap-1">
                      <span className="text-slate-400 mr-1">Keys: </span>
                      {result.keysUsed?.map((k) => (
                        <span key={k} className={`${pnClass(k)} ring-2 ring-amber-400`} data-testid={`key-pn-${k}`}>
                          {k}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center flex-wrap gap-1">
                      <span className="text-slate-400 mr-1">Runners: </span>
                      {[...(result.runnersUsed || [])].sort((a, b) => a - b).map((r) => (
                        <span key={r} className={pnClass(r)} data-testid={`runner-pn-chip-${r}`}>
                          {r}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Tickets Table */}
                  <div className="border border-slate-700 rounded overflow-hidden max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700 bg-slate-800">
                          <TableHead className="text-slate-300 p-2">Combo</TableHead>
                          <TableHead className="text-slate-300 text-right p-2">Amt</TableHead>
                          {result.payouts?.length > 0 && (
                            <TableHead className="text-slate-300 text-right p-2">Payout</TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {result.tickets?.map((ticket, idx) => {
                          // Check if this ticket is a winner
                          const poolName = mapPoolToName(selectedPool);
                          const normalizedTicketCombo = normalizeCombo(ticket.combo);
                          
                          // Check for straight bet tickets (WIN,X or PLC,X)
                          const isStraightBet = ticket.combo.startsWith('WIN') || ticket.combo.startsWith('PLC');
                          let matchingPayout = null;
                          
                          if (isStraightBet) {
                            const [betType, runner] = ticket.combo.split(',');
                            const straightPoolName = betType === 'WIN' ? 'win' : 'place';
                            matchingPayout = result.payouts?.find(p => 
                                normPool(p.pool) === straightPoolName && p.winners?.trim() === runner
                            );
                          } else {
                            matchingPayout = result.payouts?.find(p => 
                              normPool(p.pool) === poolName && normalizeCombo(p.winners) === normalizedTicketCombo
                            );
                          }
                          
                          const isWinner = !!matchingPayout;
                          const payoutReturn = isWinner ? (ticket.amount * matchingPayout.payout) : 0;
                          
                          return (
                            <TableRow key={idx} className={`border-slate-700 ${isWinner ? "bg-green-900/20" : ""}`}>
                              <TableCell className={`font-mono p-2 ${isWinner ? "text-green-400 font-bold" : "text-slate-100"}`}>
                                {ticket.combo}
                              </TableCell>
                              <TableCell className="text-right text-emerald-400 p-2">
                                ${ticket.amount.toFixed(2)}
                              </TableCell>
                              {result.payouts?.length > 0 && (
                                <TableCell className="text-right p-2">
                                  {isWinner ? (
                                    <span className="text-green-400 font-bold">
                                      ✓ ${payoutReturn.toFixed(2)}
                                    </span>
                                  ) : (
                                    <span className="text-slate-500">-</span>
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Summary with P&L for backtesting */}
                  {result.payouts?.length > 0 ? (
                    <div className="space-y-2">
                      {(() => {
                        const poolName = mapPoolToName(selectedPool);
                        
                        // Separate exotic and straight bet tickets
                        const exoticTickets = result.tickets?.filter(t => 
                          !t.combo.startsWith('WIN') && !t.combo.startsWith('PLC')
                        ) || [];
                        const straightTickets = result.tickets?.filter(t => 
                          t.combo.startsWith('WIN') || t.combo.startsWith('PLC')
                        ) || [];
                        
                        // Calculate exotic P&L
                        let exoticWagered = 0;
                        let exoticReturned = 0;
                        exoticTickets.forEach(ticket => {
                          exoticWagered += ticket.amount;
                          const normalizedTicketCombo = normalizeCombo(ticket.combo);
                          const matchingPayout = result.payouts?.find(p => 
                            normPool(p.pool) === poolName && normalizeCombo(p.winners) === normalizedTicketCombo
                          );
                          if (matchingPayout) {
                            exoticReturned += ticket.amount * matchingPayout.payout;
                          }
                        });
                        
                        // Calculate straight bet P&L
                        let straightWagered = 0;
                        let straightReturned = 0;
                        straightTickets.forEach(ticket => {
                          straightWagered += ticket.amount;
                          // Parse combo: "WIN,3" or "PLC,3"
                          const [betType, runner] = ticket.combo.split(',');
                          const straightPoolName = betType === 'WIN' ? 'win' : 'place';
                          const matchingPayout = result.payouts?.find(p => 
                              normPool(p.pool) === straightPoolName && p.winners?.trim() === runner
                          );
                          if (matchingPayout) {
                            straightReturned += ticket.amount * matchingPayout.payout;
                          }
                        });
                        
                        const hasExotic = exoticTickets.length > 0;
                        const hasStraight = straightTickets.length > 0;

                        // A race can be "official" (some payouts posted) yet be MISSING the payout
                        // for the pool we bet — a feed gap, e.g. DEL R5 posted WIN + multi-race legs
                        // but no EX. Without that pool's result row we can't grade the play, so we
                        // must NOT paint it as a loss. Distinguish from a real loss (where the pool's
                        // payout row IS present, the winner just wasn't covered) by the row's absence.
                        const exoticResultPosted = !!result.payouts?.some(p => normPool(p.pool) === poolName);
                        const exoticUngradable = hasExotic && !exoticResultPosted;
                        // Distinguish a short-field "pool never existed" from a feed gap.
                        const ungFieldSize = result.debug?.fieldSize || 0;
                        const poolNotOffered = exoticUngradable && !poolOfferedAtField(selectedPool, ungFieldSize);

                        // Totals (exclude an ungradable exotic so it never reads as a false loss)
                        const gradedExoticWagered = exoticUngradable ? 0 : exoticWagered;
                        const gradedExoticReturned = exoticUngradable ? 0 : exoticReturned;
                        const totalWagered = gradedExoticWagered + straightWagered;
                        const totalReturned = gradedExoticReturned + straightReturned;
                        const totalPnL = totalReturned - totalWagered;
                        
                        const exoticPnL = exoticReturned - exoticWagered;
                        const straightPnL = straightReturned - straightWagered;
                        
                        return (
                          <div className="space-y-2">
                            {/* Exotic P&L row */}
                            {hasExotic && !(exoticUngradable && !hasStraight) && (
                              <div className="grid grid-cols-4 gap-1 text-xs">
                                <div className="bg-slate-800/50 p-1.5 rounded text-center">
                                  <div className="text-slate-500">{selectedPool || 'Exotic'}</div>
                                </div>
                                <div className="bg-slate-800/50 p-1.5 rounded text-center">
                                  <div className="text-slate-300">${exoticWagered.toFixed(2)}</div>
                                </div>
                                {exoticUngradable ? (
                                  <div className="col-span-2 bg-amber-900/20 border border-amber-700/40 p-1.5 rounded text-center" data-testid="exotic-ungradable-note">
                                    <div className="text-amber-400 font-semibold">
                                      {poolNotOffered
                                        ? `No ${selectedPool} offered (field of ${ungFieldSize})`
                                        : `Not graded — no ${selectedPool} payout reported`}
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div className="bg-slate-800/50 p-1.5 rounded text-center">
                                      <div className={exoticReturned > 0 ? "text-green-400" : "text-slate-400"}>
                                        ${exoticReturned.toFixed(2)}
                                      </div>
                                    </div>
                                    <div className={`p-1.5 rounded text-center ${exoticPnL >= 0 ? "bg-green-900/20" : "bg-red-900/20"}`}>
                                      <div className={exoticPnL >= 0 ? "text-green-400" : "text-red-400"}>
                                        {exoticPnL >= 0 ? "+" : ""}${exoticPnL.toFixed(2)}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                            
                            {/* Straight bet P&L row */}
                            {hasStraight && (
                              <div className="grid grid-cols-4 gap-1 text-xs">
                                <div className="bg-slate-800/50 p-1.5 rounded text-center">
                                  <div className="text-slate-500">W/P</div>
                                </div>
                                <div className="bg-slate-800/50 p-1.5 rounded text-center">
                                  <div className="text-slate-300">${straightWagered.toFixed(2)}</div>
                                </div>
                                <div className="bg-slate-800/50 p-1.5 rounded text-center">
                                  <div className={straightReturned > 0 ? "text-green-400" : "text-slate-400"}>
                                    ${straightReturned.toFixed(2)}
                                  </div>
                                </div>
                                <div className={`p-1.5 rounded text-center ${straightPnL >= 0 ? "bg-green-900/20" : "bg-red-900/20"}`}>
                                  <div className={straightPnL >= 0 ? "text-green-400" : "text-red-400"}>
                                    {straightPnL >= 0 ? "+" : ""}${straightPnL.toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Total row - only show if both types present */}
                            {hasExotic && hasStraight && (
                              <>
                                <div className="border-t border-slate-700 my-1"></div>
                                <div className="grid grid-cols-4 gap-1 text-sm">
                                  <div className="p-1.5 rounded text-center">
                                    <div className="text-slate-400 font-semibold">Total</div>
                                  </div>
                                  <div className="bg-slate-800 p-1.5 rounded text-center">
                                    <div className="font-bold text-slate-100">${totalWagered.toFixed(2)}</div>
                                  </div>
                                  <div className="bg-slate-800 p-1.5 rounded text-center">
                                    <div className={`font-bold ${totalReturned > 0 ? "text-green-400" : "text-slate-400"}`}>
                                      ${totalReturned.toFixed(2)}
                                    </div>
                                  </div>
                                  <div className={`p-1.5 rounded text-center ${totalPnL >= 0 ? "bg-green-900/30" : "bg-red-900/30"}`}>
                                    <div className={`font-bold ${totalPnL >= 0 ? "text-green-400" : "text-red-400"}`}>
                                      {totalPnL >= 0 ? "+" : ""}${totalPnL.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                            
                            {/* Single type - show standard 3-column layout */}
                            {!(hasExotic && hasStraight) && (
                              exoticUngradable && !hasStraight ? (
                                <div className="bg-amber-900/20 border border-amber-700/40 p-3 rounded text-center" data-testid="play-ungradable-note">
                                  <div className="text-amber-400 font-semibold text-sm">
                                    {poolNotOffered
                                      ? `No ${selectedPool} offered — field of ${ungFieldSize} horses. Nothing to grade.`
                                      : `Race is official, but the feed posted no ${selectedPool} payout — this play can't be graded.`}
                                  </div>
                                  <div className="text-slate-400 text-xs mt-1">
                                    ${exoticWagered.toFixed(2)} across {exoticTickets.length} tickets · {poolNotOffered ? 'pool not offered at this field size' : 'result pending / unavailable'}
                                  </div>
                                </div>
                              ) : (
                                <div className="grid grid-cols-3 gap-2 text-sm">
                                  <div className="bg-slate-800 p-2 rounded text-center">
                                    <div className="text-slate-400 text-xs">Wagered</div>
                                    <div className="font-bold text-slate-100">${totalWagered.toFixed(2)}</div>
                                  </div>
                                  <div className="bg-slate-800 p-2 rounded text-center">
                                    <div className="text-slate-400 text-xs">Returned</div>
                                    <div className={`font-bold ${totalReturned > 0 ? "text-green-400" : "text-slate-400"}`}>
                                      ${totalReturned.toFixed(2)}
                                    </div>
                                  </div>
                                  <div className={`p-2 rounded text-center ${totalPnL >= 0 ? "bg-green-900/30" : "bg-red-900/30"}`}>
                                    <div className="text-slate-400 text-xs">Net P&L</div>
                                    <div className={`font-bold ${totalPnL >= 0 ? "text-green-400" : "text-red-400"}`}>
                                      {totalPnL >= 0 ? "+" : ""}${totalPnL.toFixed(2)}
                                    </div>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        );
                      })()}
                      <div className="text-sm text-slate-300 text-right font-bold">
                        {result.tickets?.length} tickets
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-300 text-right font-bold">
                      {result.tickets?.length} tickets
                    </div>
                  )}

                  {/* Download Links */}
                  <div className="flex gap-2 justify-end mt-2">
                    <AdwButton track={selectedTrack} race={selectedRace} date={selectedDate}
                      getPools={() => ({ [selectedPool]: (result.tickets || []).map((t) => ({ combo: t.combo, amount: t.amount })) })}
                      getKeys={() => result.keysUsed || []}
                      className="h-7 px-2 text-xs border-rose-500 bg-slate-800 text-rose-300 hover:bg-slate-700" />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const amCode = selectedTrackData?.amcode || selectedTrack;
                        const content = generateAmwagerFormat(
                          amCode,
                          selectedRace,
                          selectedPool,
                          result.tickets,
                          selectedDate,
                          wheelFor(selectedPool, result.tickets, result),
                          result.poolMinimum,
                          result.ticketMin
                        );
                        downloadFile(content, `amwager${selectedDate}_${amCode}_r${selectedRace}_${selectedPool}.txt`);
                        logDownload(selectedTrack, selectedRace, selectedPool, 'amwager', result.tickets.length, result.totalWager, selectedDate, (result.tickets || []).map((t) => ({ pool: selectedPool, combo: t.combo, amount: t.amount })), ownKeyInputs());
                      }}
                      data-testid="download-amwager-btn"
                      className="h-7 px-2 text-xs border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      Amwager
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const xbCode = selectedTrackData?.xbcode || selectedTrack;
                        const content = generateXpressBetFormat(
                          xbCode,
                          selectedRace,
                          selectedPool,
                          result.tickets,
                          selectedDate,
                          wheelFor(selectedPool, result.tickets, result),
                          result.poolMinimum,
                          result.ticketMin
                        );
                        downloadFile(content, `xpressbet${selectedDate}_${xbCode}_r${selectedRace}_${selectedPool}.txt`);
                        logDownload(selectedTrack, selectedRace, selectedPool, 'xpressbet', result.tickets.length, result.totalWager, selectedDate, (result.tickets || []).map((t) => ({ pool: selectedPool, combo: t.combo, amount: t.amount })), ownKeyInputs());
                      }}
                      data-testid="download-xpressbet-btn"
                      className="h-7 px-2 text-xs border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700"
                    >
                      <Download className="h-3 w-3 mr-1" />
                      XpressBet
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-slate-500 text-center py-8">
                  Select options and generate tickets
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Performers Widget - spans all 3 columns; hidden on mobile */}
          <div className="hidden md:block lg:col-span-3">
            <TopPerformers />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickPickTester;
